import type { ReactNode, RefObject } from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useTranslation } from "@ferrivia/i18n";
import s from "./ActionDrawer.module.css";

export interface ActionDrawerShellProps {
  /** The element the panel is mounted into — the workspace's context column, so it covers that column and nothing else. */
  container: HTMLElement | null;
  title: string;
  onClose: () => void;
  footer: ReactNode;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** Focused on open instead of Radix's default first focusable. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function ActionDrawerShell({ container, title, onClose, footer, onKeyDown, initialFocusRef, children }: ActionDrawerShellProps) {
  const { t } = useTranslation("clinic");
  if (!container) {
    return null;
  }
  return (
    <RadixDialog.Root open onOpenChange={(open) => (open ? undefined : onClose())}>
      <RadixDialog.Portal container={container}>
        <RadixDialog.Content
          className={s.drawer}
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          onOpenAutoFocus={(event) => {
            if (!initialFocusRef?.current) {
              return;
            }
            event.preventDefault();
            // preventScroll: the panel is still translated off-screen mid-slide, and a plain
            // focus() scrolls the column sideways to reveal it, jerking the whole workspace.
            initialFocusRef.current.focus({ preventScroll: true });
          }}
        >
          <div className={s.head}>
            <RadixDialog.Title className={s.headTitle}>{title}</RadixDialog.Title>
            <RadixDialog.Close className={s.close} aria-label={t("actionDrawer.close", "Close")}>
              <X aria-hidden size={18} />
            </RadixDialog.Close>
          </div>
          <div className={s.body}>{children}</div>
          {footer ? <div className={s.foot}>{footer}</div> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
